import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { CustomValidators } from '@shared/components/base-form-control/custom-validators';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { InputComponent } from '@shared/components/input/input.component';
import { ModalComponent } from '@shared/components/modals/modal/modal.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { ChangeLogItemStatusConstants } from '@shared/constants/status.constants';
import { FormErrorDirective } from '@shared/directives/form-error.directive';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import {
  ChangeLogItemStatus,
  DocumentType,
  EntityType,
  listChangeOrderNosQuery,
  TemplateType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';

import { ChangeOrderService } from '../../change-order/state/change-order.service';
import { ChangeLogQuery } from '../state/change-log.query';
import { ChangeLogService } from '../state/change-log.service';

export interface ChangeLogItemDialogComponentInput {
  change_log_id: string;
  change_log_item_id?: string;
  cnfNums?: Record<string, number>;
  formValues?: {
    cause?: string;
    change_log_item_status?: ChangeLogItemStatus;
    change_order_reference?: string;
    cnf_no?: string;
    description?: string;
    investigator_fee?: string;
    is_planned?: boolean;
    is_sponsor?: boolean;
    passthrough_fee?: string;
    request_date?: string;
    requester?: string;
    service_fee?: string;
    start_date?: string;
  };
  mode?: 'create' | 'update';
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ModalComponent,
    NgClass,
    InputComponent,
    NgSelectComponent,
    FormErrorDirective,
    NgOptionComponent,
    FileManagerComponent,
    FileViewerComponent,
    AsyncPipe,
  ],
  selector: 'aux-change-log-item-dialog',
  templateUrl: './change-log-item-dialog.component.html',
})
export class ChangeLogItemDialogComponent {
  ref = inject(CustomOverlayRef<unknown, ChangeLogItemDialogComponentInput>);

  private apiService = inject(ApiService);
  private changeLogQuery = inject(ChangeLogQuery);
  private changeLogService = inject(ChangeLogService);
  private changeOrderService = inject(ChangeOrderService);
  private formBuilder = inject(UntypedFormBuilder);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private vendorsQuery = inject(OrganizationQuery);

  loading = signal(false);

  activeTabIndex = 0;
  changeOrdersNumbers: { change_order_no: string; id: string }[] = [];
  cnfNums: Record<string, number> = {};
  customErrorMessages = {
    duplicatedCNF: 'CNF # must be unique.',
  };
  fgNewCNF = this.formBuilder.group({
    cause: '',
    change_log_item_status: ChangeLogItemStatus.STATUS_NO_STATUS,
    change_order_reference: null,
    cnf_no: '',
    description: '',
    investigator_fee: '0',
    is_planned: true,
    is_sponsor: false,
    passthrough_fee: '0',
    request_date: null,
    requester: 'REQUESTER_UNKNOWN',
    service_fee: '0',
    start_date: null,
  });
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  mode: 'create' | 'update' = 'create';
  statusConstant = ChangeLogItemStatusConstants.config;
  tabs: { label: string; show: Observable<boolean> }[] = [
    {
      label: 'New CNF Item',
      show: of(true),
    },
  ];
  templateLoading$ = new BehaviorSubject(false);

  constructor() {
    if (this.ref.data?.mode) {
      this.mode = this.ref.data.mode;
    }

    if (this.ref.data?.cnfNums) {
      this.cnfNums = this.ref.data.cnfNums;
    }

    this.changeOrderService
      .listChangeOrders()
      .pipe(takeUntilDestroyed())
      .subscribe((change_order) => {
        change_order.data?.forEach((changeOrder: listChangeOrderNosQuery) => {
          this.changeOrdersNumbers.push({
            change_order_no: changeOrder.change_order_no,
            id: changeOrder.id,
          });
        });

        if (this.ref.data?.formValues) {
          this.fgNewCNF.patchValue(this.ref.data.formValues);
        }
      });
  }

  async downloadBulkTemplate() {
    this.templateLoading$.next(true);
    const { data, success } = await this.apiService.getTemplatePath(
      null,
      TemplateType.BULK_INVOICE_TEMPLATE
    );

    if (!(success && data)) {
      this.overlayService.error('There was a problem downloading the template');
    } else {
      await this.apiService.downloadFileFromPath(data.id);
    }

    this.templateLoading$.next(false);
  }

  duplicateCNFValidator(cnfNums: Record<string, number>): ValidatorFn {
    return (control: AbstractControl): null | ValidationErrors => {
      const duplicate = !!Object.entries(cnfNums).filter(([, val]) => {
        return control.value ? val.toString() === (control.value as number).toString() : false;
      }).length;

      return duplicate ? { duplicatedCNF: { value: control.value } } : null;
    };
  }

  get getCnfValidators(): ValidatorFn[] {
    return [
      Validators.required,
      CustomValidators.mustBeInteger(),
      this.duplicateCNFValidator(this.cnfNums),
    ];
  }

  get getDateValidator(): ValidatorFn[] {
    return [CustomValidators.minMaxDate()];
  }

  getFilePathForCisItems() {
    const trialId = this.mainQuery.getValue().trialKey;
    const vendorId = this.vendorsQuery.getActiveId() || '';
    const changeLogId = this.changeLogQuery.getActiveId() || '';

    return () =>
      `trials/${trialId}/vendors/${vendorId}/changelogs/${changeLogId}/changelogitems/${this.fgNewCNF.controls.cnf_no.value}`;
  }

  getFilePaths(text: string, type: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return () => `trials/${trialId}/sites/${text}/${type}/`;
  }

  getMetadata(): () => FileMetadata {
    return () => ({
      admin: '1',
      documentType: DocumentType.DOCUMENT_CNF_LOG_ITEM,
      entity_id: this.fgNewCNF.controls.cnf_no.value,
      entity_type_id: EntityType.CLI,
    });
  }

  get getMustNumericValidator(): ValidatorFn[] {
    return [CustomValidators.mustHaveNumericValues()];
  }

  onCancel() {
    this.ref.close();
  }

  async onNewCNFSubmit() {
    const { id } = this.mainQuery.getSelectedTrial()?.sponsor_organization || {};
    const change_log = this.changeLogQuery.getActive();

    if (this.fgNewCNF.valid && this.ref.data) {
      this.loading.set(true);
      const submitData = this.fgNewCNF.value;
      const { data, success } = await this.changeLogService.addLogItem({
        cause: submitData.cause,
        change_log_id: this.ref.data.change_log_id,
        change_log_item_status: submitData.change_log_item_status,
        change_order_reference: submitData.change_order_reference,
        cnf_no: submitData.cnf_no,
        description: submitData.description,
        investigator_fee: submitData.investigator_fee ? +submitData.investigator_fee : null,
        is_planned: submitData.is_planned,
        organization_id: (submitData.is_sponsor ? id : change_log?.vendor_id) || '',
        passthrough_fee: submitData.passthrough_fee ? +submitData.passthrough_fee : null,
        request_date: submitData.request_date,
        requester: submitData.requester,
        service_fee: submitData.service_fee ? +submitData.service_fee : null,
        start_date: submitData.start_date,
      });

      if (success && data) {
        this.loading.set(false);
        void this.onUpload(data.id);
        this.overlayService.success('Change log successfully created.');
        this.ref.close(true);
      }
    }
  }

  onTabChange(i: number) {
    if (this.activeTabIndex === i) {
      return;
    }

    this.activeTabIndex = i;
  }

  async onUpdateClick() {
    const { id } = this.mainQuery.getSelectedTrial()?.sponsor_organization || {};
    const change_log = this.changeLogQuery.getActive();

    if (this.fgNewCNF.valid && this.ref.data) {
      const updateData = this.fgNewCNF.value;

      this.loading.set(true);
      const { success } = await this.changeLogService.updateLogItem({
        cause: updateData.cause,
        change_log_id: this.ref.data.change_log_id,
        change_log_item_id: this.ref.data.change_log_item_id || '',
        change_log_item_status: updateData.change_log_item_status,
        change_order_reference:
          updateData.change_order_reference === '' ? null : updateData.change_order_reference,
        cnf_no: updateData.cnf_no,
        description: updateData.description,
        investigator_fee: updateData.investigator_fee ? updateData.investigator_fee : null,
        is_planned: updateData.is_planned,
        organization_id: (updateData.is_sponsor ? id : change_log?.vendor_id) || '',
        passthrough_fee: updateData.passthrough_fee ? updateData.passthrough_fee : null,
        request_date: updateData.request_date,
        requester: updateData.requester,
        service_fee: updateData.service_fee ? updateData.service_fee : null,
        start_date: updateData.start_date,
      });

      if (success) {
        void this.onUpload(this.ref.data.change_log_item_id || '');
        this.overlayService.success('Change log successfully updated.');
        this.ref.close(true);
      }

      this.loading.set(false);
    }
  }

  async onUpload(change_log_item_id: string) {
    if (this.fileManager) {
      const path = this.getFilePathForCisItems();
      const files = this.fileManager.fileQuery.getAll();

      if (files) {
        for (const file of files) {
          this.fileManager.fileStore.update(file.id, {
            ...file,
            bucket_key: `${path()}${file.bucket_key}`,
          });
        }

        const fileSuccess = await this.fileManager.fileService.uploadFiles({
          insertDocument: true,
          metadata: {
            documentType: DocumentType.DOCUMENT_CNF_LOG_ITEM,
            entity_id: change_log_item_id,
            entity_type_id: EntityType.CLI,
          },
          showSuccess: false,
        });

        if (fileSuccess) {
          this.ref.close(true);
        }
      }
    }
  }
}
